/**
 * components/ui/StudentAvatar.tsx
 * ------------------------------------------------------------------
 * Reusable student avatar that shows:
 *   1. The student's photo (thumbnail or full) if available
 *   2. A styled initials fallback if no photo
 *
 * Used in: Admissions list, Dashboard search results, Student Profile
 * header, and any future screen that shows a student name.
 *
 * Sizes: 'sm' (32px, search results), 'md' (48px, lists), 'lg' (64px, profile header)
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import type { Student } from '@/types';
import { useStudentPhotoUrl } from '@/lib/studentPhoto';

type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<AvatarSize, { box: string; text: string }> = {
  sm: { box: 'w-8 h-8 rounded-lg text-xs', text: 'text-xs' },
  md: { box: 'w-12 h-12 rounded-xl text-sm', text: 'text-sm' },
  lg: { box: 'w-16 h-16 rounded-2xl text-xl', text: 'text-xl' },
};

function getInitials(name: string): string {
  return (name ?? '?')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

interface Props {
  student: Student;
  size?: AvatarSize;
  className?: string;
}

export default function StudentAvatar({ student, size = 'md', className = '' }: Props) {
  /* Fetched through the storage rules — never a stored shareable link. A
     viewer the rules refuse simply sees initials. */
  const photoUrl = useStudentPhotoUrl(student.photo, size === 'lg' ? 'full' : 'thumb');
  const [imgError, setImgError] = useState(false);
  const s = SIZE_CLASSES[size];

  if (photoUrl && !imgError) {
    return (
      <img
        src={photoUrl}
        alt={student.fullName}
        onError={() => setImgError(true)}
        className={`${s.box} object-cover shrink-0 ${className}`}
      />
    );
  }

  // Initials fallback
  return (
    <div
      className={`${s.box} bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0 ${className}`}
    >
      {getInitials(student.fullName)}
    </div>
  );
}
